import { proposalCheckoutEstimateId } from '@/lib/sales/proposal-navigation';
import { ProposalCheckout } from '@/components/public/ProposalCheckout';
import { postChooseOptionForDeposit } from '@/routes/(public)/proposal.$id';
import { useQueryClient } from '@tanstack/react-query';
import { opsProposalKeys } from '@/lib/queryKeys';
import { useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Loader2, X } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/components/atoms/button';
import { ProposalContent } from '@/routes/(public)/proposal.$id';
import type { ProposalSelectionInput } from '@/routes/(public)/proposal.$id';
import { useProposalPresentation } from '@/hooks/proposals/useProposalPresentation';
import { useAcceptProposalInPerson } from '@/hooks/proposals/useAcceptProposalInPerson';
import { registerSignedProposalPdf } from '@/lib/proposals/registerSignedProposalPdf';
import type { ProposalStatus } from '@/types/proposal';

export const Route = createFileRoute('/_dashboard/dashboard/proposals/$id/present')({
  component: PresentProposalRoute,
});

function PresentProposalRoute() {
  const { id } = Route.useParams();
  return <PresentProposalPage id={id} />;
}

/** The statuses the customer may act on — the same set the public page uses. */
const SELECTABLE_STATUSES: ProposalStatus[] = ['SENT', 'VIEWED', 'FOLLOW_UP'];

/**
 * Presentation mode — the office opens the proposal, hands the device to the
 * customer, and the customer reviews every option and accepts on the spot.
 *
 * The customer-facing body is literally the public page's `ProposalContent`,
 * so what they read here is what the emailed link would have shown: each
 * option's included work, its totals, the terms, and the acceptance gate. Any
 * other rendering would be a second customer document to keep in step with the
 * first.
 *
 * It covers the dashboard rather than living beside it: a sidebar full of
 * office navigation is not something to hand to a customer, and the chrome is
 * one deliberate Exit control.
 */
/**
 * Exported for tests: the TanStack router plugin rewrites route components in
 * the vitest pipeline, so the route itself can't be rendered directly (same
 * reason proposal.$id.tsx exports ProposalContent). The id arrives as a prop so
 * the page under test is the real one, wiring included.
 */
export function PresentProposalPage({ id }: { id: string }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [choosingDeposit, setChoosingDeposit] = useState(false);
  // The CUSTOMER's projection, not the dashboard row — see the hook. The page
  // renders the public content component, which decides the signature gate
  // from fields only that projection carries.
  const { data: proposal, isLoading, isError } = useProposalPresentation(id);
  const acceptMutation = useAcceptProposalInPerson();

  const [termsAccepted, setTermsAccepted] = useState(false);

  // One source for what is on screen: the accept writes the customer's
  // projection back into this query, so there is no second copy in local state
  // to fall out of step with it.
  const current = proposal;
  const exit = () =>
    void navigate({
      to: '/dashboard/proposals/$id',
      params: { id },
      search: (prev) => prev as never,
    });

  const handleSelect = async (optionIndex: number, selection?: ProposalSelectionInput) => {
    if (!current || acceptMutation.isPending || choosingDeposit) return;
    if (selection?.depositFirst || selection?.checkoutFirst) {
      setChoosingDeposit(true);
      try {
        const chosen = await postChooseOptionForDeposit(
          id,
          optionIndex,
          selection.acceptMembershipOffer === true,
          selection.membershipBillingOptionId,
          selection.lineItems,
          selection.expectedTotal,
        );
        queryClient.setQueryData(opsProposalKeys.presentation(id), chosen);
      } catch (error) {
        toast.error(error instanceof Error ? error.message : 'Could not continue to payment.');
      } finally {
        setChoosingDeposit(false);
      }
      return;
    }
    if (!termsAccepted) {
      toast.error('Please accept the terms to continue');
      return;
    }
    // The CTA is disabled until the pad and name are filled; this is the
    // backstop for a stale render, mirroring the server's own refusal.
    if (current.signatureRequired && !selection?.signature) {
      toast.error('Please type your name and draw your signature to continue');
      return;
    }
    acceptMutation.mutate(
      {
        id,
        optionIndex,
        ...(selection?.expectedTotal != null ? { expectedTotal: selection.expectedTotal } : {}),
        signature: selection?.signature ?? null,
        lineItems: selection?.lineItems,
        acceptMembershipOffer: selection?.acceptMembershipOffer === true,
        membershipBillingOptionId: selection?.membershipBillingOptionId ?? null,
      },
      {
        onSuccess: (result) => {
          setTermsAccepted(false);
          toast.success('Accepted. The estimate is ready.');
          // The signature only exists as an image in THIS browser, so the
          // signed document has to be made here. Best-effort: the acceptance
          // is already recorded and durable without it.
          if (selection?.signature) {
            void registerSignedProposalPdf({
              opsBaseUrl: import.meta.env.VITE_OPERATIONS_URL ?? '',
              id,
              reason: 'signature_collected',
              content: {
                proposal: {
                  ...result.proposal,
                  signatureDataUrl: selection.signature.image,
                },
                company: {
                  name: result.proposal.company?.name ?? 'Your provider',
                  logoUrl: result.proposal.company?.logoUrl ?? null,
                },
              },
            }).catch(() => undefined);
          }
        },
      },
    );
  };

  if (isLoading) {
    return (
      <PresentationShell onExit={exit}>
        <div className="flex justify-center py-20">
          <Loader2 className="size-8 animate-spin text-muted-foreground" />
        </div>
      </PresentationShell>
    );
  }

  if (isError || !current) {
    return (
      <PresentationShell onExit={exit}>
        <p className="py-20 text-center text-sm text-muted-foreground">
          This proposal could not be loaded.
        </p>
      </PresentationShell>
    );
  }

  const selectedIndex = current.selectedOptionIndex ?? null;
  const canSelect = SELECTABLE_STATUSES.includes(current.status) && selectedIndex == null;

  return (
    <PresentationShell onExit={exit} estimateId={proposalCheckoutEstimateId(current) ?? null}>
      <ProposalContent
        proposal={current}
        checkout={
          proposalCheckoutEstimateId(current) ? (
            <ProposalCheckout
              proposal={current}
              onAccepted={(accepted) =>
                queryClient.setQueryData(opsProposalKeys.presentation(id), accepted)
              }
            />
          ) : null
        }
        selectedIndex={selectedIndex}
        canSelect={canSelect}
        submitting={acceptMutation.isPending || choosingDeposit}
        showSuccessBanner={selectedIndex != null}
        // Presentation is a one-way step: a customer who has accepted on the
        // tech's device does not un-accept on it. Re-opening the decision is
        // an office action (revise), not a control to hand over.
        canChange={false}
        onChangeSelection={() => undefined}
        // A refusal is recorded by the office after the visit, from the
        // proposal's own Decline action — not on the device being handed over.
        canDecline={false}
        showDeclineForm={false}
        declineReason=""
        onDeclineReasonChange={() => undefined}
        onToggleDeclineForm={() => undefined}
        onDecline={() => undefined}
        onSelect={handleSelect}
        termsAccepted={termsAccepted}
        onTermsAcceptedChange={setTermsAccepted}
      />
    </PresentationShell>
  );
}

/**
 * The full-viewport frame. Fixed over the dashboard so no office navigation is
 * on screen while the customer is holding the device, while the route stays
 * inside `_dashboard` and keeps its authentication.
 */
function PresentationShell({
  children,
  onExit,
  estimateId,
}: {
  children: React.ReactNode;
  onExit: () => void;
  estimateId?: string | null;
}) {
  const navigate = useNavigate();
  return (
    <div className="fixed inset-0 z-[100] overflow-y-auto bg-gray-50">
      <div className="sticky top-0 z-10 flex items-center justify-between gap-3 border-b bg-white px-4 py-2">
        <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
          Presenting to customer
        </p>
        <div className="flex items-center gap-2">
          {estimateId ? (
            <Button
              size="sm"
              variant="outline"
              onClick={() =>
                void navigate({
                  to: '/dashboard/estimates/$id',
                  params: { id: estimateId },
                  search: { tab: 'overview' } as never,
                })
              }
            >
              Open estimate
            </Button>
          ) : null}
          <Button size="sm" variant="ghost" onClick={onExit}>
            <X className="mr-1 size-4" />
            Exit
          </Button>
        </div>
      </div>
      <div className="mx-auto w-full max-w-6xl p-4 sm:p-6">{children}</div>
    </div>
  );
}
